import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { db } from "../firebase";
import {
  getDoc,
  doc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import Menu from "../pages/Profile-Popup/ScreeningMenu";
import { useAuth } from "./AuthContext";
import ContactSupport from "../pages/Profile-Popup/ContactSupport";
import Popup from "./Popup";
import { useSearchParams, useNavigate } from "react-router-dom";
import EmergencyBanner from "./Notifications/EmergencyBanner";
import NotificationPanel from "./Notifications/NotificationPanel";
import { useNotifications } from "./Notifications/useNotifications";
import menuBtn from "../assets/disaster-icons/menu-btn.svg";
import down from "../assets/disaster-icons/down.svg";
import fireIcon from "../assets/caseManagement/header/fire-icon.png";
import earthQuakeIcon from "../assets/caseManagement/header/earthquake-icon.png";
import floodIcon from "../assets/caseManagement/header/flood-icon.png";
import hurricaneIcon from "../assets/caseManagement/header/hurricane-icon.png";
import tornadoIcon from "../assets/caseManagement/header/tornado-icon.png";
import volcanoIcon from "../assets/caseManagement/header/volcano-icon.png";
import activeIcon from "../assets/caseManagement/header/active-icon.png";
import inActiveIcon from "../assets/caseManagement/header/in-active-icon.png";
import { useDisasterObjContext } from "../context/DisasterObjContext";
import notificationIcon from "../assets/disaster-icons/notification.svg";
import cancelIcon from "../assets/caseManagement/header/cancel-icon.png";
import Modal from "./Modal";

const disasterAvatar = {
  Earthquake: {
    icon: earthQuakeIcon,
    color: "rgba(121, 88, 29, 0.19)",
  },
  Fire: {
    icon: fireIcon,
    color: "rgba(194, 60, 60, 0.19)",
  },
  Flood: {
    icon: floodIcon,
    color: "rgba(45, 174, 223, 0.19)",
  },
  Hurricane: {
    icon: hurricaneIcon,
    color: "rgba(78, 118, 238, 0.19)",
  },
  Tornado: {
    icon: tornadoIcon,
    color: "rgba(202, 156, 45, 0.19)",
  },
  Volcano: {
    icon: volcanoIcon,
    color: "rgba(237, 135, 39, 0.19)",
  },
  "Winter Storm": {
    icon: floodIcon,
    color: "rgba(162, 217, 255, 0.19)",
  },
  Wildfire: {
    icon: fireIcon,
    color: "rgba(255, 162, 162, 0.19)",
  },
  "Hazmat Spill": {
    icon: fireIcon,
    color: "rgba(185, 185, 185, 0.19)",
  },
  "Civil Unrest": {
    icon: fireIcon,
    color: "rgba(255, 205, 162, 0.19)",
  },
  "Power Outage": {
    icon: fireIcon,
    color: "rgba(255, 255, 162, 0.19)",
  },
  "Cyber Attack": {
    icon: fireIcon,
    color: "rgba(162, 255, 162, 0.19)",
  },
  Other: {
    icon: fireIcon,
    color: "rgba(229, 162, 255, 0.19)",
  },
};

const ScreeningNavbar = ({ setToggleSidebar }) => {
  const dropdownRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const [data, setData] = useState(null);
  const { role } = useAuth();
  const [searchParams] = useSearchParams();
  const isDupAddress = searchParams.get("dupAddress") === "true";
  const [showPopup, setShowPopup] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { notifications, unreadNotifications, emergencyNotification } =
    useNotifications();
  const navigate = useNavigate();
  const selectedUserId = sessionStorage.getItem("userId");
  const [disasterList, setDisasterList] = useState([]);
  const [screeningNotifs, setScreeningNotifs] = useState([]);
  const [showNotifsDetails, setShowNotifsDetails] = useState(false);
  const [selectedNotifs, setSelectedNotifs] = useState({});

  const { disaster: activeDisaster, setDisaster: setActiveDisaster } =
    useDisasterObjContext();

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate();
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const onGetActiveDisaster = useCallback(async () => {
    if (!userId) return;

    const userDocRef = doc(db, "users", userId);
    const userQuery = await getDoc(userDocRef);

    if (!userQuery.exists()) return;

    const userData = userQuery.data();

    // Handle both partner and center cases
    if (userData.user_role === "partner") {
      // Get partner data
      const partnerDocRef = doc(db, "partners", userData.partner_id);
      const partnerQuery = await getDoc(partnerDocRef);
      if (!partnerQuery.exists()) return;

      const partnerData = partnerQuery.data();

      // Get center IDs (handle both array and single value)
      const centerIds = Array.isArray(userData.center_id)
        ? userData.center_id
        : [userData.center_id];

      if (centerIds.length === 0) {
        console.log("No center IDs available");
        return;
      }

      // Get counties from area_serviced_county
      const counties = partnerData.area_serviced_county || [];

      // If no counties found, fall back to single county
      if (counties.length === 0) {
        const singleCounty = partnerData.county;
        if (singleCounty) {
          // Use the traditional approach with single county
          const getDeclaredDisasters = await getDocs(
            collection(db, "declared-disaster")
          );

          const disasters = getDeclaredDisasters.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            ?.filter(
              ({ affected_counties, is_deleted }) =>
                affected_counties?.includes(singleCounty) && !is_deleted
            );

          handleDisasterResults(disasters);
        }
        return;
      }

      // Split counties into chunks of 10 (Firestore limit)
      const chunks = [];
      for (let i = 0; i < counties.length; i += 10) {
        chunks.push(counties.slice(i, i + 10));
      }

      // Track unique disaster IDs
      const seenIds = new Set();
      const disasters = [];

      // Query for each chunk of counties
      for (const chunk of chunks) {
        const q = query(
          collection(db, "declared-disaster"),
          where("is_deleted", "==", false),
          where("affected_counties", "array-contains-any", chunk)
        );

        const snapshot = await getDocs(q);

        snapshot.forEach((doc) => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            disasters.push({
              id: doc.id,
              ...doc.data(),
            });
          }
        });
      }

      handleDisasterResults(disasters);
    } else {
      // Center case - keep existing logic
      const centerDocRef = doc(db, "centers", userData.center_id);
      const centerQuery = await getDoc(centerDocRef);
      if (!centerQuery.exists()) return;
      const organizationCounty = centerQuery.data().county;

      const getDeclaredDisasters = await getDocs(
        collection(db, "declared-disaster")
      );

      const disasters = getDeclaredDisasters.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        ?.filter(
          ({ affected_counties, is_deleted }) =>
            affected_counties?.includes(organizationCounty) && !is_deleted
        );

      handleDisasterResults(disasters);
    }
  }, [userId]);

  // Helper function to handle disaster results
  const handleDisasterResults = (disasters) => {
    if (disasters.length > 0) {
      setDisasterList(disasters);

      // Get stored disaster or set the first available one as default
      const storedDisasterId = sessionStorage.getItem("activeDisasterId");
      console.log("storedDisasterId", storedDisasterId);

      if (
        !storedDisasterId ||
        !disasters.find(({ id }) => id === storedDisasterId)
      ) {
        sessionStorage.setItem("activeDisasterId", disasters[0].id);
        setActiveDisaster(disasters[0]);
        window.dispatchEvent(new Event("storage"));
      } else {
        setActiveDisaster(disasters.find(({ id }) => id === storedDisasterId));
      }
    }
  };

  useEffect(() => {
    if (!!userId) {
      fetchData();
      onGetActiveDisaster();
    }
  }, [userId, onGetActiveDisaster]);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser && selectedUserId) {
        setUserId(selectedUserId);
      } else {
        setData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  useEffect(() => {
    const getNotifs = async () => {
      const employeeDetails = JSON.parse(
        sessionStorage.getItem("employeeDetails")
      );

      const { role, id } = employeeDetails || {};

      const isSuperVisor = role === "Screening Supervisor";

      const notifsRef = collection(db, "message-center");
      const notifQuery = query(
        notifsRef,
        where("type", "==", "screening_supervisor_message")
      );

      const notifSubs = onSnapshot(notifQuery, (snapShot) => {
        const notifsData = snapShot.docs
          .map((doc) => {
            return {
              id: doc.id,
              ...doc.data(),
            };
          })
          .filter((nd) => !nd?.viewer?.includes(id));

        console.log({ notifsData });

        if (isSuperVisor) {
          setScreeningNotifs(() => notifsData || []);
        }
      });

      return () => notifSubs();
    };

    getNotifs();
  }, []);

  const onOpenNotifs = useCallback(async (id, isActive, viewer = []) => {
    try {
      const employeeDetails = JSON.parse(
        sessionStorage.getItem("employeeDetails")
      );

      const { id: empId } = employeeDetails || {};

      await updateDoc(doc(db, "message-center", id), {
        active: false,
        viewer: [...viewer, empId],
      });

      setShowNotifications(() => false);
      setShowNotifsDetails(() => true);
    } catch (error) {
      console.log({ NotifsError: error });
    }
  }, []);

  const fetchData = async () => {
    try {
      const userDocRef = doc(db, "users", selectedUserId);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (isDupAddress) setShowPopup(true);

        if (userData.user_role === "center") {
          const centerDocRef = doc(db, "centers", userData.center_id);
          const centerDocSnap = await getDoc(centerDocRef);
          if (centerDocSnap.exists()) {
            setData({ id: centerDocSnap.id, ...centerDocSnap.data() });
          }
        } else if (userData.user_role === "partner") {
          const partnerDocRef = doc(db, "partners", userData.partner_id);
          const partnerDocSnap = await getDoc(partnerDocRef);
          if (partnerDocSnap.exists()) {
            setData({ id: partnerDocSnap.id, ...partnerDocSnap.data() });
          }
        } else {
          setData({ id: userDocSnap.id, ...userDocSnap.data() });
        }
      } else {
      }
    } catch (error) {}
  };

  const [showMenu, setShowMenu] = useState(false);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const [showSupport, setShowSupport] = useState(false);

  const toggleSupport = () => {
    setShowSupport(!showSupport);
  };

  const renderActiveDisaster = useMemo(() => {
    return !!activeDisaster ? (
      <div className="flex items-center space-x-3">
        {disasterAvatar?.[activeDisaster.disaster_type] && (
          <div
            className="h-[30px] w-[30px] translate-x-3 overflow-hidden rounded-full p-1"
            style={{
              backgroundColor:
                disasterAvatar?.[activeDisaster.disaster_type]?.color,
            }}
          >
            <img
              src={disasterAvatar?.[activeDisaster.disaster_type]?.icon}
              className="h-full w-full object-contain"
            />
          </div>
        )}
        <select
          className="border-0 bg-transparent px-[12px] py-[9px] text-center text-[18px]"
          value={activeDisaster.id}
          onChange={(e) => {
            const getDisaster = disasterList.find(
              ({ id }) => id === e.target.value
            );
            if (getDisaster) {
              setActiveDisaster(getDisaster);
              sessionStorage.setItem("activeDisasterId", getDisaster.id);
              window.dispatchEvent(new Event("storage"));
            }
          }}
        >
          {disasterList.map(({ id, disaster_name }) => {
            return (
              <option key={id} value={id}>
                {disaster_name}
              </option>
            );
          })}
        </select>
        <div className="h-[30px] w-[30px]">
          <img
            src={
              activeDisaster?.disaster_status === "active"
                ? activeIcon
                : inActiveIcon
            }
            className="h-full w-full object-contain"
          />
        </div>
      </div>
    ) : (
      <></>
    );
  }, [activeDisaster, disasterList]);

  return (
    <>
      <nav className="shadow-none">
        <div className="fixed left-0 right-0 top-0 z-10 flex h-[6vh] w-full flex-row items-center justify-between bg-[#F7CA41] text-left font-poppins text-base leading-[normal] tracking-[normal] shadow-none">
          <div className="flex h-[6vh] flex-row items-center justify-center gap-[11px] pl-7">
            <button
              className="h-[24px] w-[24px] cursor-pointer bg-transparent"
              onClick={() => setToggleSidebar?.((prev) => !prev)}
            >
              <img
                src={menuBtn}
                alt="Menu"
                className="h-full w-full object-contain"
              />
            </button>
            <div className="flex flex-col items-start font-semibold text-[inherit]">
              <div className="flex flex-col items-baseline text-white">
                <p className="header-casemanagement-label">DARCI Screening</p>
              </div>
            </div>
          </div>

          {renderActiveDisaster}

          {/* <EmergencyBanner notification={emergencyNotification} /> */}
          {/* <NotificationPanel
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          notifications={notifications}
        /> */}
          <div className="flex max-h-[6vh] flex-col items-center justify-center pr-7 text-right text-xs text-honeydew">
            <div className="flex flex-row items-center justify-center gap-[14px]">
              {role === "citizen" && (
                <div
                  onClick={() => navigate("/opportunities/donate")}
                  className="flex cursor-pointer items-center justify-center"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="24" height="24" rx="6" fill="#F9B54C" />
                    <path
                      d="M13.4286 19.3688C13.5683 19.4063 13.7302 19.4219 13.9143 19.4156C14.0984 19.4094 14.254 19.3812 14.381 19.3312L20 17.25C20 16.825 19.8476 16.4687 19.5429 16.1812C19.2381 15.8937 18.8825 15.75 18.4762 15.75H13.2571C13.219 15.75 13.1746 15.7469 13.1238 15.7406C13.073 15.7344 13.0349 15.725 13.0095 15.7125L11.8857 15.3188C11.7841 15.2813 11.7143 15.2188 11.6762 15.1313C11.6381 15.0438 11.6317 14.95 11.6571 14.85C11.6825 14.7625 11.746 14.6938 11.8476 14.6438C11.9492 14.5938 12.0508 14.5875 12.1524 14.625L13.0095 14.9438C13.0603 14.9688 13.1016 14.9844 13.1333 14.9906C13.1651 14.9969 13.2127 15 13.2762 15H15.2762C15.5175 15 15.7302 14.9188 15.9143 14.7563C16.0984 14.5938 16.1905 14.3812 16.1905 14.1187C16.1905 13.9437 16.1365 13.775 16.0286 13.6125C15.9206 13.45 15.7778 13.3375 15.6 13.275L10.3238 11.3438C10.2349 11.3187 10.146 11.2969 10.0571 11.2781C9.96825 11.2594 9.87937 11.25 9.79048 11.25H8.57143V18.0187L13.4286 19.3688ZM4 18C4 18.4125 4.14921 18.7656 4.44762 19.0594C4.74603 19.3531 5.10476 19.5 5.52381 19.5C5.94286 19.5 6.30159 19.3531 6.6 19.0594C6.89841 18.7656 7.04762 18.4125 7.04762 18V12.75C7.04762 12.3375 6.89841 11.9844 6.6 11.6906C6.30159 11.3969 5.94286 11.25 5.52381 11.25C5.10476 11.25 4.74603 11.3969 4.44762 11.6906C4.14921 11.9844 4 12.3375 4 12.75V18ZM15.4286 12.15C15.2381 12.15 15.0508 12.1156 14.8667 12.0469C14.6825 11.9781 14.5143 11.875 14.3619 11.7375L12.2667 9.7125C11.873 9.3375 11.5397 8.92187 11.2667 8.46562C10.9937 8.00937 10.8571 7.5125 10.8571 6.975C10.8571 6.2875 11.1016 5.70313 11.5905 5.22188C12.0794 4.74063 12.673 4.5 13.3714 4.5C13.7778 4.5 14.1587 4.58437 14.5143 4.75313C14.8698 4.92188 15.1746 5.15 15.4286 5.4375C15.6825 5.15 15.9873 4.92188 16.3429 4.75313C16.6984 4.58437 17.0794 4.5 17.4857 4.5C18.1841 4.5 18.7778 4.74063 19.2667 5.22188C19.7556 5.70313 20 6.2875 20 6.975C20 7.5125 19.8667 8.00937 19.6 8.46562C19.3333 8.92187 19.0032 9.3375 18.6095 9.7125L16.4952 11.7375C16.3429 11.875 16.1746 11.9781 15.9905 12.0469C15.8063 12.1156 15.619 12.15 15.4286 12.15Z"
                      fill="#A2211A"
                    />
                  </svg>
                </div>
              )}
              <div
                className="relative flex items-center justify-center"
                ref={dropdownRef}
              >
                <button
                  className="h-[25px] w-[25px] cursor-pointer bg-transparent"
                  onClick={() => setShowNotifications(() => true)}
                >
                  <img
                    src={notificationIcon}
                    className="h-full w-full object-contain"
                  />
                  {!!screeningNotifs.length && (
                    <div className="absolute -right-1.5 -top-1.5 h-[14px] w-[14px] rounded-full bg-red-500" />
                  )}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 top-[110%] flex h-[500px] min-w-[400px] flex-col overflow-y-auto rounded-lg bg-white shadow-2xl">
                    <div className="flex items-center justify-between bg-[#C4DAF3] p-4">
                      <p className="text-start text-xl font-bold text-[#04447A]">
                        Notifications
                      </p>
                      <button
                        className="flex h-[32px] w-[32px] cursor-pointer items-center justify-center bg-transparent"
                        onClick={() => setShowNotifications(() => false)}
                      >
                        <img
                          src={cancelIcon}
                          className="h-full w-full object-contain"
                        />
                      </button>
                    </div>
                    <div className="flex flex-1 flex-col items-center overflow-y-auto">
                      {!screeningNotifs.length ? (
                        <div className="w-full">
                          <p className="p-4 text-center text-[14px] text-black">
                            There are no notifications.
                          </p>
                        </div>
                      ) : (
                        screeningNotifs.map((props, i) => {
                          const {
                            title,
                            content,
                            sender,
                            active,
                            id,
                            timestamp,
                            viewer,
                          } = props || {};
                          return (
                            <div
                              className={`flex w-full cursor-pointer flex-col space-y-2 border border-solid border-[#CFCFCF] p-4 ${
                                active ? "bg-blue-50" : ""
                              }`}
                              onClick={() => {
                                setSelectedNotifs(() => props);
                                onOpenNotifs(id, active, viewer);
                              }}
                              key={i}
                            >
                              <div className="flex items-center justify-between border border-solid">
                                <p className="text-start text-[18px] font-bold text-black">
                                  {title}
                                </p>
                                <p className="text-start text-[13px] font-bold text-black">
                                  {formatDateTime(timestamp)}
                                </p>
                              </div>
                              <div>
                                <p className="text-start text-[16px] text-black">
                                  From: {sender.email}
                                </p>
                                <p className="text-start text-[16px] text-black">
                                  {content}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* <NotificationButtonScreening
              unreadNotifications={unreadNotifications}
              onClick={() => setShowNotifications(true)}
            /> */}
              <div className="box-border flex h-[25px] w-[25px] cursor-pointer flex-col items-start justify-start px-0 pb-0  text-center">
                <div className="bg-gold flex h-[25px]  w-[25px] flex-row items-center justify-center self-stretch rounded-md">
                  <div className="relative hidden h-[25px] w-[25px] rounded-md bg-white " />
                  <p className="relative z-[1] font-medium text-[inherit] [text-decoration:none]">
                    {selectedUserId &&
                      (data?.first_name
                        ? data?.first_name.charAt(0)
                        : role === "partner"
                        ? "P"
                        : role === "center"
                        ? "C"
                        : role === "admin"
                        ? "A"
                        : "")}
                  </p>
                </div>
              </div>
              <div className="flex flex-col">
                <p
                  className="font-Inter relative inline-block cursor-pointer font-semibold text-[#242424]"
                  onClick={toggleMenu}
                >
                  {selectedUserId &&
                    (data?.first_name
                      ? data?.first_name
                      : // eslint-disable-next-line no-useless-concat
                      "First Name" + " " + data?.last_name
                      ? data?.last_name
                      : "Last Name")}
                </p>
              </div>
              <div
                className="box-border flex h-[25px] w-[25px] cursor-pointer flex-col items-start justify-start px-0 pb-0  text-center"
                onClick={toggleMenu}
              >
                <img src={down} alt="down" width="28" height="28" />
              </div>
            </div>
          </div>
        </div>
        <Menu isOpen={showMenu} onClose={toggleMenu} data={data} />
        <ContactSupport
          isOpen={showSupport}
          onClose={toggleSupport}
          data={data}
        />
        {showPopup && (
          <Popup
            title="Duplicate Address Notification"
            message="The duplicate address registration has been recorded. Thank you for reporting this activity."
            onClose={() => setShowPopup(false)}
          />
        )}
      </nav>
      <Modal
        title={selectedNotifs?.title || ""}
        width="600px"
        isOpen={showNotifsDetails}
        onClose={() => setShowNotifsDetails(() => false)}
        noFooter
      >
        <div className="flex flex-col space-y-2">
          <p className="text-2xl font-bold">{selectedNotifs?.content || ""}</p>
          <p>{formatDateTime(selectedNotifs?.timestamp)}</p>
          <p>From: {selectedNotifs?.sender?.email || ""}</p>
        </div>
      </Modal>
    </>
  );
};

export default ScreeningNavbar;
